import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CloudArrowDownIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  ClockIcon,
} from '@heroicons/react/20/solid'
import { sapIntegrationApi, type SapConfigUpdate, type SapSyncLog } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString()
}

function formatDuration(ms: number | null): string {
  if (!ms) return '-'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60000).toFixed(1)}m`
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    connected: 'bg-green-100 text-green-800',
    success: 'bg-green-100 text-green-800',
    completed: 'bg-green-100 text-green-800',
    not_configured: 'bg-gray-100 text-gray-800',
    failed: 'bg-red-100 text-red-800',
    expired: 'bg-yellow-100 text-yellow-800',
    partial: 'bg-yellow-100 text-yellow-800',
    started: 'bg-blue-100 text-blue-800',
  }

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  )
}

function ConnectionForm({
  onSave,
  onTest,
  loading,
  testLoading,
  initialData,
}: {
  onSave: (data: SapConfigUpdate) => void
  onTest: (data: { serviceLayerUrl: string; companyDb: string; username: string; password: string }) => void
  loading?: boolean
  testLoading?: boolean
  initialData?: {
    serviceLayerUrl: string | null
    companyDb: string | null
    username: string | null
    hasPassword: boolean
  }
}) {
  const [serviceLayerUrl, setServiceLayerUrl] = useState(initialData?.serviceLayerUrl || '')
  const [companyDb, setCompanyDb] = useState(initialData?.companyDb || '')
  const [username, setUsername] = useState(initialData?.username || '')
  const [password, setPassword] = useState('')
  const { t } = useTranslation()

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    const data: SapConfigUpdate = {
      serviceLayerUrl: serviceLayerUrl || null,
      companyDb: companyDb || null,
      username: username || null,
    }
    if (password) {
      data.password = password
    }
    onSave(data)
  }

  const handleTest = () => {
    if (serviceLayerUrl && companyDb && username && password) {
      onTest({ serviceLayerUrl, companyDb, username, password })
    } else if (serviceLayerUrl && companyDb && username && initialData?.hasPassword) {
      // Test with saved credentials
      onTest({ serviceLayerUrl: '', companyDb: '', username: '', password: '' })
    }
  }

  const canTest = serviceLayerUrl && companyDb && username && (password || initialData?.hasPassword)

  return (
    <form onSubmit={handleSave} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">Service Layer URL</label>
        <input
          type="url"
          value={serviceLayerUrl}
          onChange={(e) => setServiceLayerUrl(e.target.value)}
          placeholder="https://sap.example.com:50000/b1s/v1"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        <p className="mt-1 text-xs text-gray-500">SAP Business One Service Layer endpoint URL</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Company Database</label>
        <input
          type="text"
          value={companyDb}
          onChange={(e) => setCompanyDb(e.target.value)}
          placeholder="SBODEMOUS"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Username</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Password {initialData?.hasPassword && <span className="text-gray-400">(saved)</span>}
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={initialData?.hasPassword ? '********' : ''}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
        <button
          type="button"
          onClick={handleTest}
          disabled={testLoading || !canTest}
          className="flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${testLoading ? 'animate-spin' : ''}`} />
          Test Connection
        </button>
      </div>
    </form>
  )
}

function SyncSettings({
  config,
  onUpdate,
  loading,
}: {
  config: {
    productSyncEnabled: boolean
    productSyncFrequency: string
    productPriceLists: number[] | null
    orderPushEnabled: boolean
    orderPushTriggerStatus: string
  }
  onUpdate: (data: SapConfigUpdate) => void
  loading?: boolean
}) {
  const [productSyncEnabled, setProductSyncEnabled] = useState(config.productSyncEnabled)
  const [productSyncFrequency, setProductSyncFrequency] = useState(config.productSyncFrequency)
  const [orderPushEnabled, setOrderPushEnabled] = useState(config.orderPushEnabled)
  const [orderPushTriggerStatus, setOrderPushTriggerStatus] = useState(config.orderPushTriggerStatus)
  const { t } = useTranslation()

  const handleSave = () => {
    onUpdate({
      productSyncEnabled,
      productSyncFrequency,
      orderPushEnabled,
      orderPushTriggerStatus,
    })
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
          <CloudArrowDownIcon className="h-5 w-5 text-gray-400" />
          Product Sync (SAP to JustSell)
        </h4>
        <div className="mt-4 space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="productSyncEnabled"
              checked={productSyncEnabled}
              onChange={(e) => setProductSyncEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <label htmlFor="productSyncEnabled" className="ml-2 block text-sm text-gray-700">
              Enable automatic product sync
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Sync Frequency</label>
            <select
              value={productSyncFrequency}
              onChange={(e) => setProductSyncFrequency(e.target.value)}
              disabled={!productSyncEnabled}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm disabled:bg-gray-100"
            >
              <option value="realtime">Real-time (every 5 min)</option>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="manual">Manual only</option>
            </select>
          </div>
        </div>
      </div>

      <div className="pb-4">
        <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
          <CloudArrowUpIcon className="h-5 w-5 text-gray-400" />
          Order Push (JustSell to SAP)
        </h4>
        <div className="mt-4 space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="orderPushEnabled"
              checked={orderPushEnabled}
              onChange={(e) => setOrderPushEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <label htmlFor="orderPushEnabled" className="ml-2 block text-sm text-gray-700">
              Enable automatic order push to SAP
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Push when order status is</label>
            <select
              value={orderPushTriggerStatus}
              onChange={(e) => setOrderPushTriggerStatus(e.target.value)}
              disabled={!orderPushEnabled}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm disabled:bg-gray-100"
            >
              <option value="confirmed">Confirmed</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">Orders will be created in SAP when they reach this status</p>
          </div>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={loading}
        className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 disabled:opacity-50"
      >
        {loading ? t('common.saving') : 'Save Settings'}
      </button>
    </div>
  )
}

function SyncStatusPanel({
  status,
  onSyncProducts,
  onPushOrders,
  syncLoading,
}: {
  status: {
    productSync: {
      lastSync: string | null
      lastStatus: string | null
      lastCount: number | null
      lastError: string | null
    }
    orderPush: {
      lastPush: string | null
      lastStatus: string | null
    }
  }
  onSyncProducts: (full: boolean) => void
  onPushOrders: () => void
  syncLoading: boolean
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-gray-50 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Product Sync</h4>
            <p className="mt-1 text-sm text-gray-500">
              Last sync: {formatDate(status.productSync.lastSync)}
              {status.productSync.lastStatus && (
                <> - <StatusBadge status={status.productSync.lastStatus} /></>
              )}
              {status.productSync.lastCount !== null && (
                <span className="ml-2 text-gray-400">({status.productSync.lastCount} records)</span>
              )}
            </p>
            {status.productSync.lastError && (
              <p className="mt-1 text-sm text-red-600">{status.productSync.lastError}</p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onSyncProducts(false)}
              disabled={syncLoading}
              className="flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${syncLoading ? 'animate-spin' : ''}`} />
              Delta Sync
            </button>
            <button
              onClick={() => onSyncProducts(true)}
              disabled={syncLoading}
              className="flex items-center gap-1 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-brand-500 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${syncLoading ? 'animate-spin' : ''}`} />
              Full Sync
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-gray-50 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Order Push</h4>
            <p className="mt-1 text-sm text-gray-500">
              Last push: {formatDate(status.orderPush.lastPush)}
              {status.orderPush.lastStatus && (
                <> - <StatusBadge status={status.orderPush.lastStatus} /></>
              )}
            </p>
          </div>
          <button
            onClick={onPushOrders}
            disabled={syncLoading}
            className="flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            <CloudArrowUpIcon className="h-4 w-4" />
            Push Pending Orders
          </button>
        </div>
      </div>
    </div>
  )
}

function RecentLogsTable({ logs }: { logs: SapSyncLog[] }) {
  if (logs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <ClockIcon className="mx-auto h-8 w-8 text-gray-400" />
        <p className="mt-2 text-sm">No sync logs yet</p>
      </div>
    )
  }

  return (
    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 uppercase">Type</th>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 uppercase">Status</th>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 uppercase">Records</th>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 uppercase">Duration</th>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 uppercase">Started</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {logs.map((log) => (
            <tr key={log.id}>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                <span className="flex items-center gap-1">
                  {log.direction === 'pull' ? (
                    <CloudArrowDownIcon className="h-4 w-4 text-blue-500" />
                  ) : (
                    <CloudArrowUpIcon className="h-4 w-4 text-green-500" />
                  )}
                  {log.syncType.replace(/_/g, ' ')}
                </span>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm">
                <StatusBadge status={log.status} />
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {log.recordsProcessed > 0 && (
                  <span>
                    {log.recordsProcessed} processed
                    {log.recordsCreated > 0 && <span className="text-green-600"> (+{log.recordsCreated})</span>}
                    {log.recordsUpdated > 0 && <span className="text-blue-600"> (~{log.recordsUpdated})</span>}
                    {log.recordsFailed > 0 && <span className="text-red-600"> (!{log.recordsFailed})</span>}
                  </span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {formatDuration(log.durationMs)}
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {formatDate(log.startedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function SapIntegrationPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null)

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['sap-config'],
    queryFn: sapIntegrationApi.getConfig,
  })

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['sap-status'],
    queryFn: sapIntegrationApi.getStatus,
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  const updateConfigMutation = useMutation({
    mutationFn: sapIntegrationApi.updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sap-config'] })
      queryClient.invalidateQueries({ queryKey: ['sap-status'] })
    },
  })

  const testConnectionMutation = useMutation({
    mutationFn: (data: { serviceLayerUrl: string; companyDb: string; username: string; password: string }) =>
      sapIntegrationApi.testConnection(data.serviceLayerUrl ? data : undefined),
    onSuccess: (result) => {
      setTestResult(result)
      queryClient.invalidateQueries({ queryKey: ['sap-status'] })
      setTimeout(() => setTestResult(null), 5000)
    },
    onError: (error) => {
      setTestResult({ success: false, message: error instanceof Error ? error.message : 'Test failed' })
      setTimeout(() => setTestResult(null), 5000)
    },
  })

  const syncProductsMutation = useMutation({
    mutationFn: sapIntegrationApi.syncProducts,
    onSuccess: (result) => {
      setSyncResult(result)
      queryClient.invalidateQueries({ queryKey: ['sap-status'] })
      setTimeout(() => setSyncResult(null), 5000)
    },
    onError: (error) => {
      setSyncResult({ success: false, message: error instanceof Error ? error.message : 'Sync failed' })
      setTimeout(() => setSyncResult(null), 5000)
    },
  })

  const pushOrdersMutation = useMutation({
    mutationFn: sapIntegrationApi.pushOrders,
    onSuccess: (result) => {
      setSyncResult(result)
      queryClient.invalidateQueries({ queryKey: ['sap-status'] })
      setTimeout(() => setSyncResult(null), 5000)
    },
    onError: (error) => {
      setSyncResult({ success: false, message: error instanceof Error ? error.message : 'Push failed' })
      setTimeout(() => setSyncResult(null), 5000)
    },
  })

  if (configLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">SAP Integration</h1>
          <p className="mt-2 text-sm text-gray-700">
            Configure SAP Business One integration for product sync and order management.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          {config && (
            <div className="flex items-center gap-2">
              {config.connectionStatus === 'connected' ? (
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
              ) : config.connectionStatus === 'failed' ? (
                <XCircleIcon className="h-5 w-5 text-red-500" />
              ) : (
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
              )}
              <StatusBadge status={config.connectionStatus} />
            </div>
          )}
        </div>
      </div>

      {/* Result notifications */}
      {testResult && (
        <div className={`mt-4 rounded-md p-4 ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
          <div className="flex">
            {testResult.success ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-400" />
            )}
            <p className={`ml-3 text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
              {testResult.message}
            </p>
          </div>
        </div>
      )}

      {syncResult && (
        <div className={`mt-4 rounded-md p-4 ${syncResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
          <div className="flex">
            {syncResult.success ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-400" />
            )}
            <p className={`ml-3 text-sm font-medium ${syncResult.success ? 'text-green-800' : 'text-red-800'}`}>
              {syncResult.message}
            </p>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Left column - Connection & Settings */}
        <div className="space-y-8">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Cog6ToothIcon className="h-5 w-5 text-gray-400" />
              Connection Settings
            </h3>
            {config && (
              <ConnectionForm
                initialData={{
                  serviceLayerUrl: config.serviceLayerUrl,
                  companyDb: config.companyDb,
                  username: config.username,
                  hasPassword: config.hasPassword,
                }}
                onSave={(data) => updateConfigMutation.mutate(data)}
                onTest={(data) => testConnectionMutation.mutate(data)}
                loading={updateConfigMutation.isPending}
                testLoading={testConnectionMutation.isPending}
              />
            )}
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <ArrowPathIcon className="h-5 w-5 text-gray-400" />
              Sync Settings
            </h3>
            {config && (
              <SyncSettings
                config={{
                  productSyncEnabled: config.productSyncEnabled,
                  productSyncFrequency: config.productSyncFrequency,
                  productPriceLists: config.productPriceLists,
                  orderPushEnabled: config.orderPushEnabled,
                  orderPushTriggerStatus: config.orderPushTriggerStatus,
                }}
                onUpdate={(data) => updateConfigMutation.mutate(data)}
                loading={updateConfigMutation.isPending}
              />
            )}
          </div>
        </div>

        {/* Right column - Status & Logs */}
        <div className="space-y-8">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Sync Status</h3>
            {status && (
              <SyncStatusPanel
                status={status.config}
                onSyncProducts={(full) => syncProductsMutation.mutate(full)}
                onPushOrders={() => pushOrdersMutation.mutate()}
                syncLoading={syncProductsMutation.isPending || pushOrdersMutation.isPending}
              />
            )}
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
            {status && <RecentLogsTable logs={status.recentLogs} />}
          </div>
        </div>
      </div>
    </div>
  )
}
